
import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';
import { generateDealNumber } from '@/utils/dateUtils';

interface UseTariffSelectionProps {
  amoCustomerCode?: string | null;
  amoDealCode?: string | null; 
  clientNumber?: string | null;
  onSuccess?: (tariff: { title: string; price: number }) => void;
}

interface TariffData {
  title: string;
  price: number; 
  key: string;
}

// Соответствие между ключами тарифов в приложении и enum_id для webhook
const tariffKeyToEnumId: Record<string, string> = {
  'week': 'weekly',
  '2weeks': 'biweekly',
  'month': 'monthly',
  'buyout': 'rent-to-own'
};

export const useTariffSelection = ({
  amoCustomerCode,
  amoDealCode,
  clientNumber,
  onSuccess
}: UseTariffSelectionProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'idle' | 'sending' | 'success' | 'error' | 'timeout'>('idle');
  const [retryCount, setRetryCount] = useState(0);
  const { user } = useAuth();

  const sendTariff = async (selectedTariff: TariffData) => {
    if (!selected) return;

    setLoading(true);
    setResponseStatus('sending');
    console.log(`Отправка запроса на тариф: ${selectedTariff.title}, цена: ${selectedTariff.price}`);
    console.log('Телефон пользователя:', user?.phone);
    
    try {
      // Генерируем номер сделки
      const dealNumber = generateDealNumber();
      
      // Получаем enum_id для выбранного тарифа
      const tariffEnumId = tariffKeyToEnumId[selectedTariff.key] || selectedTariff.key;
      
      // Формируем данные для отправки в вебхук
      const payload = {
        data: {
          phone: user?.phone || null,
          tariff: selectedTariff.title,
          price: selectedTariff.price,
          dealNumber: dealNumber,
          tariffType: tariffEnumId // Добавляем enum_id тарифа
        }
      };

      const webhookUrl = 'https://hook.eu2.make.com/ibit73nie2ftbuw4n7vnt55f1xoc17mp';
      
      if (webhookUrl) {
        console.log('Отправка данных на webhook:', webhookUrl);
        console.log('Данные для отправки:', JSON.stringify(payload));
        
        // Создаем контроллер отмены и устанавливаем таймаут
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд тайм-аут
        
        try {
          // Отправляем запрос с контроллером для отмены
          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload),
            signal: controller.signal
          });
          
          // Очищаем таймаут, так как запрос выполнен
          clearTimeout(timeoutId);
          
          console.log('Webhook запрос отправлен успешно, статус:', response.status);
          setResponseStatus('success');
          
          // Проверяем ответ
          let responseData;
          try {
            responseData = await response.json();
            console.log('Данные ответа:', responseData);
          } catch (parseError) {
            console.log('Ответ не содержит JSON:', await response.text());
          }
          
          if (response.ok) {
            toast.success(`Тариф "${selectedTariff.title}" выбран!`, {
              description: "Ожидайте ссылку на оплату в течение минуты",
              duration: 60000 // 1 минута для администраторов
            });

            setTimeout(() => {
              toast.info("Ссылка на оплату будет отправлена в течение минуты", {
                duration: 5000
              });
            }, 3000);
            
            if (onSuccess) {
              onSuccess(selectedTariff);
            }
          } else {
            throw new Error(`Ошибка вебхука: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError: any) {
          clearTimeout(timeoutId);
          
          if (fetchError.name === 'AbortError') {
            console.error("Превышено время ожидания ответа от сервера");
            setResponseStatus('timeout');
            setRetryCount(prev => prev + 1);
            
            toast.error("Превышено время ожидания ответа", {
              description: "Сервер не ответил вовремя. Нажмите кнопку для повторной отправки запроса.",
              duration: 60000,
              action: {
                label: "Повторить",
                onClick: () => sendTariff(selectedTariff)
              }
            });
          } else {
            console.error("Ошибка при отправке запроса:", fetchError);
            setResponseStatus('error');
            
            toast.error("Ошибка при выборе тарифа", {
              description: "Пожалуйста, попробуйте еще раз позже",
              duration: 60000,
              action: {
                label: "Повторить",
                onClick: () => sendTariff(selectedTariff)
              }
            });
          }
        }
      }
    } catch (error: any) {
      console.error("Ошибка при выборе тарифа:", error);
      setResponseStatus('error');
      
      toast.error("Произошла ошибка при выборе тарифа", {
        description: "Детали ошибки: " + (error.message || "Неизвестная ошибка"),
        duration: 60000
      });
    } finally {
      setLoading(false);
    }
  };

  // Добавлен метод для повторной отправки запроса
  const retryRequest = (selectedTariff: TariffData) => {
    if (responseStatus === 'timeout' || responseStatus === 'error') {
      toast.info("Повторная отправка запроса...");
      sendTariff(selectedTariff);
    }
  };

  return {
    selected,
    setSelected,
    sendTariff,
    loading,
    responseStatus,
    retryCount,
    retryRequest
  };
};
